import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { isApiError } from '@/api/errors';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Notice } from '@/components/Notice';
import { loginStaff } from '@/auth/actions';
import { roleHome } from '@/auth/roles';
import { useStaffSession } from '@/auth/react';
import { describeError } from '@/lib/describe-error';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';
import { staffEndNotice } from './guards';

/** Only same-app absolute paths are accepted as a return target. */
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/login')) return null;
  return value;
}

export const WRONG_CREDENTIALS = "That email or password isn't right. Try again.";

/**
 * Words for a failed sign-in. A 401 from the login endpoint means the email or password was wrong, which
 * is said plainly; every other failure (throttled, offline, server) keeps its own accurate message.
 */
export function describeLoginError(error: unknown): { message: string; credentials: boolean } {
  if (isApiError(error) && !error.isNetworkError && error.status === 401) {
    return { message: WRONG_CREDENTIALS, credentials: true };
  }
  const { title, message } = describeError(error);
  return { message: `${title}. ${message}`, credentials: false };
}

export function Login() {
  const session = useStaffSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<{ message: string; credentials: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice] = useState(staffEndNotice);

  const from = safeReturnPath((location.state as { from?: unknown } | null)?.from);

  if (session) return <Navigate to={from ?? roleHome(session.user.role)} replace />;

  const emailError = attempted && email.trim() === '' ? 'Enter your email.' : undefined;
  const passwordError = attempted && password === '' ? 'Enter your password.' : undefined;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    setFailure(null);
    if (email.trim() === '' || password === '' || busy) return;
    setBusy(true);
    try {
      const next = await loginStaff({ email, password });
      void navigate(from ?? roleHome(next.user.role), { replace: true });
    } catch (e) {
      setFailure(describeLoginError(e));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center justify-between gap-3 px-4 md:px-6">
          <span className="flex items-center gap-2 text-base font-bold text-ink">
            <span
              aria-hidden="true"
              className="grid size-7 place-items-center rounded-control bg-primary text-sm text-white"
            >
              S
            </span>
            ServeFlow
          </span>
          <Link
            to="/"
            className="inline-flex min-h-11 items-center gap-1 rounded-control px-2 text-sm font-semibold text-primary no-underline hover:bg-primary-soft"
          >
            <Icon name="chevronLeft" size={16} />
            Back to ServeFlow
          </Link>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="flex flex-1 flex-col items-center justify-center px-4 py-8 md:py-12"
      >
        <section
          aria-labelledby="login-title"
          className={cn(
            cardClasses({ padding: 'none' }),
            'flex w-full max-w-md flex-col gap-6 p-6 md:p-8',
          )}
        >
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold tracking-wide text-primary uppercase">ServeFlow</p>
            <h1 id="login-title" className="text-2xl leading-8 font-bold text-ink">
              Staff sign in
            </h1>
            <p className="text-sm text-muted">
              For waiters, the kitchen, the counter and managers. Guests order by scanning the QR
              code at their table or seat.
            </p>
          </div>
          {notice && <Notice tone="warn">{notice}</Notice>}
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              invalid={failure?.credentials}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              invalid={failure?.credentials}
            />
            {failure && <Notice tone="error">{failure.message}</Notice>}
            <Button type="submit" variant="primary" size="lg" fullWidth loading={busy}>
              Sign in
            </Button>
          </form>
        </section>
      </main>
    </div>
  );
}
